import styled, { css } from "styled-components";
import { Device } from "../../../../components/device";
import { isLight } from "../../../../util/ColorHelper";

export type ButtonSize = "small" | "medium" | "large";
export type ButtonAlignment = "left" | "center" | "right";

interface ButtonProps {
    size?: ButtonSize;
    align?: ButtonAlignment;
    className?: string;
    data?: string;
}

const BaseButton = styled.button<ButtonProps>`
    border-radius: 0;
    border-width: 2px;
    border-style: solid;
    border-color: currentColor;
    color: currentColor;
    cursor: pointer;
    display: inline-flex;
    font-size: var(--text-small, 13px);
    font-weight: bold;
    letter-spacing: .075em;
    line-height: 1.25;
    margin-bottom: .5rem;
    min-width: 10.5rem;
    justify-content: ${props =>
        (props.align === "left") ? "flex-start" :
            (props.align === "right") ? "flex-end" :
                "center"};
    overflow: hidden;
    padding: .75em 1.5em;
    position: relative;
    user-select: none;
    text-decoration: none;
    text-transform: uppercase;
    transition: all .4s;
    z-index: 0;

    &:focus {
        outline: none;
    }

    ${props => isLight(props.theme.backgroundColor) ? css`` : null};

    &.m-0 {
        margin: 0;
    }

    &.mb-0 {
        margin-bottom: 0;
    }
`;

BaseButton.defaultProps = {
    className: "btn"
};

export const PrimaryButton = styled(BaseButton)`
    background-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
    border-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
    color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-white, white)" : "var(--color-black, black)"};

    &:before {
        background-color: black;
        content: "";
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 50%;
        max-width: 0;
        transform: translateX(-50%);
        transition: max-width .2s;
        z-index: -1;
    }

    &:hover {
        box-shadow: var(--shadow-inner);
        background-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "black"};
        border-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "black"};
        color: white;

        &:before {
            max-width: 100%;
        }
    }

    ${props => props.size === "large" ? css`
        font-size: var(--text-lg, 1.133rem);
    ` : null}

    ${props => props.size === "small" ? css`
        font-size: var(--text-xs, .7333rem);
        min-width: 0;
    ` : null}
`;

export const SecondaryButton = styled(BaseButton)`
    background-color: transparent;
    border-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
    color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-black, black)" : "var(--color-white, white)"};

    &:before {
        background-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
        content: "";
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 50%;
        z-index: -1;
        transform: translateX(-50%);
        max-width: 0;
        transition: max-width .2s;
    }

    &:hover {
        border-color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
        color: ${props => isLight(props.theme.backgroundColor) ? "var(--color-white, white)" : "var(--color-black, black)"};

        &:before {
            max-width: 100%;
        }
    }

    ${props => props.size === "large" ? css`
        font-size: var(--text-lg, 1.133rem);
    ` : null}

    ${props => props.size === "small" ? css`
        font-size: var(--text-xs, .7333rem);
        min-width: 0;
    ` : null}
`;


export const TertiaryButton = styled(BaseButton)`
    background-color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
    border-color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-gray-60, black)" : "var(--color-gray-10, white)"};
    color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-white, white)" : "var(--color-black, black)"};

    &:before {
        background-color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-black, black)" : "var(--color-white, white)"};
        content: "";
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 50%;
        z-index: -1;
        transform: translateX(-50%);
        max-width: 0;
        transition: max-width .2s;
    }

    &:hover {
        border-color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-black, black)" : "var(--color-white, white)"};
        color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-gray-20, white)" : "var(--color-black, black)"};

        &:before {
            max-width: 100%;
        }
    }

    ${props => props.size === "large" ? css`
        font-size: var(--text-lg, 1.133rem);
    ` : null}

    ${props => props.size === "small" ? css`
        font-size: var(--text-xs, .7333rem);
        min-width: 0;
    ` : null}
`;

export const CTAButton = styled(BaseButton)`
    border-color: transparent;
    color: currentColor;
    display: inline-block;
    opacity: .75;

    &:after {
        content: "";
        background-color: currentColor;
        display: inline-block;
        width: 100%;
        height: 2px;
        transition: max-width .2s;
        max-width: 50px;
    }

    &:hover {
        opacity: 1;

        &:after {
            max-width: 100%;
        }
    }

    ${props => props.size === "large" ? css`
        font-size: 1.267rem;
    ` : null}

    ${props => props.size === "small" ? css`
        font-size: var(--text-xs, .7333rem);
        min-width: 0;
    ` : null}
`;

export const DropdownButton = styled(BaseButton)`
    border: none;
    border-bottom: 2px solid var(--color-gray-30, gray);
    color: currentColor;
    font-weight: 400;
    font-size: 1rem;
    min-width: 120px;
    padding: .65rem 1.2rem .65rem 0;
    letter-spacing: 0;
    vertical-align: middle;
    text-align: left;
    text-transform: none;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        top: 50%;
        pointer-events: none;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 5px solid currentColor;
    }

    &:focus,
    &:hover,
    &:active {
        border-color: ${props =>
        isLight(props.theme.backgroundColor) ? "var(--color-black, black)" : "var(--color-white, white)"};
    }
`;

export const IconButton = styled(BaseButton)`
    background-color: transparent;
    color: currentColor;
    height: 3rem;
    width: 3rem;
    border-radius: 50%;
    display: inline-flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    padding: 0;
    line-height: 1;
    letter-spacing: 0;
    vertical-align: middle;
    opacity: .66;

    &:focus {
        transform: scale(.95);
    }
    
    &:hover {
        opacity: 1;
    }

    ${Device.mobileMax} {
        height: 2.5rem;
        width: 2.5rem;
    }
`;
